import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { formatDateDE } from "@/lib/utils";
import { SignaturePad } from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => Number(v || 0).toFixed(2).replace(".", ",");

export default function StundennachweisBestaetigen() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [signature, setSignature] = useState("");
  const [haken, setHaken] = useState(false);
  // Am Handy/Tablet wird mit dem Finger unterschrieben, am PC reicht Name + Häkchen
  const touch = typeof window !== "undefined"
    && window.matchMedia?.("(hover: none) and (pointer: coarse)").matches;
  const [perFinger, setPerFinger] = useState(!!touch);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/stundennachweise/by-token/${token}`);
        if (!mounted) return;
        setData(d);
        if (d.confirmed_at) {
          setConfirmed(true);
          setName(d.confirmed_by || "");
        } else {
          setName(d.kontakt_name || "");
        }
      } catch {
        if (mounted) setError("Dieser Bestätigungslink ist ungültig oder abgelaufen.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const confirm = async () => {
    if (name.trim().length < 2) { toast.error("Bitte Ihren Namen angeben"); return; }
    if (perFinger && !signature) { toast.error("Bitte im Feld unterschreiben"); return; }
    if (!perFinger && !haken) { toast.error("Bitte das Häkchen zur Bestätigung setzen"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/stundennachweise/by-token/${token}/confirm`, {
        name: name.trim(),
        signature_data_url: perFinger ? signature : "",
        per_klick: !perFinger,
      });
      setConfirmed(true);
      toast.success("Vielen Dank — Stunden bestätigt.");
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="nachweis-error">
          <p className="text-xs uppercase tracking-[0.25em] text-red-700">Fehler</p>
          <h1 className="font-serif text-3xl mt-4">{error}</h1>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-stundennachweis" className="min-h-screen bg-[#FAFAF7] py-12 md:py-20">
      <div className="max-w-2xl mx-auto px-5 md:px-10">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Lukas Stutz — Mietkoch</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-4">
          Arbeitsstunden bestätigen
        </h1>
        <p className="text-sm text-[#757067] mt-3">Nachweis-Nr. <b className="text-[#BA6D52]">{data.nachweis_number}</b></p>

        <div className="mt-8 bg-white border border-[#E3DECE] p-6 md:p-8" data-testid="nachweis-details">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Einsatz</p>
          <p className="font-serif text-2xl mt-2">{data.betrieb}</p>

          <div className="mt-6 divide-y divide-[#E3DECE] text-sm">
            <div className="flex justify-between py-3">
              <span className="text-[#757067]">Einsatztag</span>
              <span data-testid="nachweis-datum">{formatDateDE(data.datum)}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-[#757067]">Arbeitszeit</span>
              <span>{data.von} – {data.bis} Uhr{data.pause_min ? ` (Pause ${data.pause_min} Min.)` : ""}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-[#757067]">Arbeitsstunden</span>
              <span className="font-serif text-xl text-[#BA6D52]" data-testid="nachweis-hours">
                {eur(data.hours)} Std.
              </span>
            </div>
            {data.stundensatz_eur > 0 && (
              <>
                <div className="flex justify-between py-3">
                  <span className="text-[#757067]">Stundensatz</span>
                  <span>{eur(data.stundensatz_eur)} € netto</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-[#757067]">Summe netto</span>
                  <span data-testid="nachweis-total">{eur(data.total_net_eur)} €</span>
                </div>
              </>
            )}
            {data.notiz && (
              <div className="py-3">
                <span className="text-[#757067] block mb-1">Notiz</span>
                <span className="italic">{data.notiz}</span>
              </div>
            )}
          </div>
        </div>

        {confirmed ? (
          <div data-testid="nachweis-confirmed" className="mt-8 bg-emerald-50 border border-emerald-200 p-6 md:p-8 text-center">
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <h2 className="font-serif text-2xl mt-4 text-emerald-900">Stunden bestätigt</h2>
            <p className="text-sm text-emerald-800 mt-2">
              Vielen Dank{data.confirmed_by ? `, ${data.confirmed_by}` : ""}! Die Bestätigung wurde mit
              Zeitstempel dokumentiert. Die Rechnung folgt separat.
            </p>
          </div>
        ) : (
          <div className="mt-8 bg-[#FAF5F1] border border-[#BA6D52] p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Bestätigung</p>
            <p className="text-sm text-[#2C2A26] mt-3 font-light leading-relaxed">
              Bitte bestätigen Sie, dass die oben aufgeführten Arbeitsstunden korrekt sind und
              durch Lukas Stutz erbracht wurden.
            </p>

            <label className="block mt-6 text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Ihr Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vor- und Nachname"
              data-testid="nachweis-name-input"
              className="mt-2 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-4 py-3 text-sm"
            />

            {perFinger ? (
              <>
                <div className="flex items-center justify-between gap-3 mt-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Unterschrift</p>
                  {!touch && (
                    <button onClick={() => setPerFinger(false)} data-testid="nachweis-modus-klick"
                      className="text-[11px] text-[#8A5A42] underline underline-offset-4 decoration-[#D9BBA6]">
                      Lieber nur bestätigen
                    </button>
                  )}
                </div>
                <div className="mt-2">
                  <SignaturePad onChange={setSignature} testId="nachweis-signature" />
                </div>
              </>
            ) : (
              <div className="mt-6">
                <label className="flex items-start gap-3 cursor-pointer bg-white border border-[#E3DECE] px-4 py-4 hover:border-[#BA6D52] transition-colors">
                  <input type="checkbox" checked={haken} data-testid="nachweis-haken"
                    onChange={(e) => setHaken(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#BA6D52]" />
                  <span className="text-sm text-[#2C2A26] leading-relaxed">
                    Ich bestätige verbindlich, dass die oben aufgeführten Arbeitsstunden korrekt
                    sind. Mit Namenseingabe und diesem Häkchen gilt der Nachweis als bestätigt —
                    eine handschriftliche Unterschrift ist dafür nicht erforderlich.
                  </span>
                </label>
                <button onClick={() => setPerFinger(true)} data-testid="nachweis-modus-unterschrift"
                  className="mt-3 text-[11px] text-[#8A5A42] underline underline-offset-4 decoration-[#D9BBA6]">
                  Ich möchte doch unterschreiben
                </button>
              </div>
            )}

            <button
              onClick={confirm}
              disabled={saving}
              data-testid="nachweis-confirm-btn"
              className="mt-6 w-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-8 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-3 transition-colors"
            >
              {saving ? (<><Loader2 size={16} className="animate-spin" /> Wird gespeichert …</>)
                      : (<><Clock size={16} /> Stunden bestätigen</>)}
            </button>
            <p className="text-xs text-[#757067] italic mt-4">
              Sollten Korrekturen nötig sein, melden Sie sich bitte direkt bei Lukas Stutz.
            </p>
          </div>
        )}

        <p className="text-xs text-[#757067] text-center mt-12">
          Lukas Stutz — Mietkoch für Gastronomie &amp; Events
        </p>
      </div>
    </div>
  );
}
